import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CallOffConfirm } from './CallOffConfirm'
import {
  fetchColleagues,
  reinstateSession,
  withdrawCover,
  type CoverState,
} from '../api/cover'
import { raiseAsk } from '../api/asks'

/**
 * The three things a teacher does about a class she is out for (D-365).
 *
 * **Extracted so two very different screens share one implementation rather
 * than two that agree today.** These lived inside `CoverPanel`, on a card below
 * her teaching week; the day dialog now needs the same three, on the row they
 * are about. Rebuilding them from the same pieces is the drift rule (D-199) —
 * a panel and a dialog would disagree first about the cancelled case, which is
 * exactly where two copies of this idea disagreed once already.
 *
 * **Primitives, not a `CoverRow`.** The dialog holds a `DaySession` and the
 * panel holds a `CoverRow`; making either construct the other's shape in order
 * to reuse a control is how a component acquires a caller it was not written
 * for. What these actually need is an id and a state.
 */


/**
 * Asking a colleague to take one session (D-328, restored by D-354).
 *
 * **Its own component with its own query**, so the colleague list is fetched
 * once the panel has an absence to ask about rather than on every schedule
 * render — most teachers have none, and most days it would be a request for a
 * list nobody opens.
 *
 * **Re-asking after a decline is the same control.** D-328's flow is declare,
 * ask, and ask again when the answer is no; a separate "ask again" would be two
 * implementations of one idea.
 */
export function AskSomebody({
  requestId,
  state,
  onAsked,
}: {
  /** The cover request being re-pointed. */
  requestId: string
  /** Only to word the button: asking again reads differently from asking. */
  state: CoverState
  onAsked: () => void
}) {
  const [open, setOpen] = useState(false)
  const [staffId, setStaffId] = useState('')

  const colleagues = useQuery({
    queryKey: ['cover', 'colleagues', requestId],
    /*
     * <b>Named by the absence, not by a date this component does not have.</b>
     * It is re-pointing one cover request, so the server resolves that request
     * to its day and marks anybody away (D-682). Without it the picker would
     * offer a name `CoverKind` then refuses - the D-424 failure, on the
     * control whose whole job is choosing somebody who can actually take it.
     */
    queryFn: () => fetchColleagues(undefined, requestId),
    enabled: open,
  })

  /*
   * **Nothing to ask about once somebody has agreed** (D-365).
   *
   * `CoverRequest.Ask` throws *"This session is already covered"*, so on an
   * `Accepted` row this control can only refuse — and the one beside it reads
   * *"Nobody can take it"*, which is false on a row where somebody
   * demonstrably has.
   *
   * **The gate lives here rather than at the call site.** `CoverPanel` gated
   * its own render on `(Uncovered || Asked)` and the dialog did not, because a
   * guard repeated at each caller is a guard the next caller forgets. A
   * component that cannot act now renders nothing instead of trusting whoever
   * mounted it. `CoverPanel.test.tsx` records that this exact gate was once
   * unbound and all fifteen of its tests stayed green.
   */
  const canAsk = state === 'Uncovered' || state === 'Asked'

  const ask = useMutation({
    mutationFn: () =>
      raiseAsk({ kind: 'cover', to: { type: 'Staff', id: staffId }, subject: { type: 'Cover', id: requestId } }),
    onSuccess: () => {
      setOpen(false)
      setStaffId('')
      onAsked()
    },
  })

  if (!canAsk) return null

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 -mx-1 mr-3 px-1 py-1.5 text-[12.5px] font-semibold text-accent hover:underline"
      >
        {state === 'Asked' ? 'Ask somebody else' : 'Ask somebody to take it'}
      </button>
    )
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <select
        value={staffId}
        onChange={(e) => setStaffId(e.target.value)}
        aria-label="Who should take it?"
        disabled={colleagues.isPending}
        className="min-h-[36px] rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[13px]"
      >
        <option value="">{colleagues.isPending ? 'Loading…' : 'Who should take it?'}</option>

        {/*
          The server already removes the caller, and the queue's own list was
          the one that offered the absent teacher as her own coverer. Filtering
          again here would be a second answer to that question (D-199).
        */}
        {/*
          Nobody who has recorded time off that day (D-681). `CoverKind`
          refuses her, so leaving her here would be a name the save rejects -
          and this control exists precisely to choose somebody who can take it.
        */}
        {(colleagues.data ?? [])
          .filter((c) => !c.away)
          .map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
      </select>

      <button
        type="button"
        disabled={!staffId || ask.isPending}
        onClick={() => ask.mutate()}
        className="min-h-[36px] rounded-lg bg-accent px-3 py-1.5 text-[13px] font-semibold text-on-accent disabled:opacity-50"
      >
        Ask
      </button>

      <button
        type="button"
        onClick={() => setOpen(false)}
        className="min-h-[36px] px-2 py-1.5 text-[12.5px] font-semibold text-ink-3 hover:text-ink"
      >
        Cancel
      </button>

      {/*
        The server writes the useful sentence — "She has already said she is out
        for that session" — and swallowing it leaves her tapping a button that
        visibly does nothing.
      */}
      {ask.isError && (
        <p role="alert" className="w-full text-[12px] text-crit">
          {(ask.error as Error).message}
        </p>
      )}

      {colleagues.isError && (
        <p role="alert" className="w-full text-[12px] text-crit">
          Could not load your colleagues just now.
        </p>
      )}
    </div>
  )
}

/**
 * Calling a class off, when nobody could take it (D-355).
 *
 * **A confirm step rather than a one-tap button**, because this is the only
 * control on the screen a family feels. Everything else here rearranges who
 * teaches; this one means nobody does.
 *
 * **Two boxes, two audiences** (D-659, the same split as D-656). The reason is
 * **The confirm itself is `CallOffConfirm`** since D-889, and so is the
 * reasoning about its two boxes — this is the composition, not the control.
 */
export function CallItOff({
  occurrenceId,
  state,
  onDone,
}: {
  occurrenceId: string
  /**
   * **Hidden once somebody has agreed to take it**, for the same reason
   * `AskSomebody` is (D-365).
   *
   * *"Nobody can take it"* is simply false on an `Accepted` row, and tapping
   * through it withdraws the cover that person agreed to **and** tells sixty
   * households the class is off — the one action in the app that cannot be
   * taken back (D-356), reached from a sentence that was not true.
   */
  state: CoverState
  onDone: () => void
}) {
  /*
   * Offered while the night still has nobody, which now includes waiting on
   * the studio (D-540): somebody has offered and may yet be turned down, so
   * "nobody can take it" is still a live answer. `Accepted` and `Withdrawn`
   * are the two where it is not.
   *
   * **This gate is the whole of what is left here** (D-889). Everything below
   * the trigger moved to `CallOffConfirm`, because the register reaches the
   * same act for a different reason and two copies would drift — first about
   * `expectANote()`, which nothing fails without.
   *
   * **One behaviour did change, and the new one is safer.** The gate used to
   * return `null` from a mounted component, so a half-typed confirm survived a
   * trip through `Accepted` and reopened with her reason still in it. It
   * unmounts now, so that state is discarded — which is what you want on the
   * one action that cannot be taken back.
   */
  if (state !== 'Uncovered' && state !== 'Asked' && state !== 'AwaitingApproval') return null

  // The dash is a literal: an HTML entity in a JSX *attribute* is not decoded,
  // so `&mdash;` here would reach the screen as those eight characters.
  return (
    <CallOffConfirm
      occurrenceId={occurrenceId}
      trigger="Nobody can take it — call it off"
      triggerClassName="mt-2 -mx-1 px-1 py-1.5 text-[12.5px] font-semibold text-ink-3 hover:text-crit"
      reasonLabel="Why? Only the studio sees this."
      reasonPlaceholder="Nobody could cover it"
      onDone={onDone}
    />
  )
}

/**
 * A class she has already called off, and the way back (D-355).
 *
 * **The undo restores the class and nothing else**, and the screen says so —
 * otherwise she puts it back on believing her substitute is still coming. The
 * cancellation withdrew every arrangement for that session and none of them
 * return.
 *
 * **Only her own.** A studio closure is Tonya's decision about the whole
 * building; offering an undo the server refuses would be a control that only
 * refuses.
 */
export function CalledOff({
  occurrenceId,
  cancelledByMe,
  onDone,
}: {
  occurrenceId: string
  cancelledByMe: boolean
  onDone: () => void
}) {
  const run = useMutation({
    mutationFn: () => reinstateSession(occurrenceId),
    onSuccess: onDone,
  })

  return (
    <div className="mt-2">
      <p className="text-[12.5px] font-semibold text-crit">
        {cancelledByMe ? 'You called this one off.' : 'The studio called this one off.'}
      </p>

      {cancelledByMe && (
        <>
          <button
            type="button"
            onClick={() => run.mutate()}
            disabled={run.isPending}
            className="mt-1 -mx-1 px-1 py-1.5 text-[12.5px] font-semibold text-accent hover:underline disabled:opacity-60"
          >
            {run.isPending ? 'Putting it back...' : 'Put it back on'}
          </button>

          <p className="text-[11.5px] text-ink-3">
            The class comes back on. Nothing is arranged &mdash; you will need to sort cover again,
            and the families are not told, so tell them yourself.
          </p>
        </>
      )}

      {run.isError && (
        <p role="alert" className="mt-1 text-[12px] text-crit">
          {(run.error as Error).message}
        </p>
      )}
    </div>
  )
}

/**
 * "I can make it after all" (D-328, rehoused by D-365).
 *
 * **It had exactly one caller, on the panel that is going**, which is the D-332
 * shape this repo has already been bitten by: an endpoint whose only way in
 * disappears. It belongs on the row, in the dialog she opened to sort the day
 * out — the same place she said she was out in the first place.
 *
 * **Not offered once the class is off.** Cancelling withdraws her declaration
 * along with everything else (D-355), so there is nothing left to withdraw and
 * the sentence would be about a class that is not happening. `Accepted` keeps
 * it: somebody agreed to cover, she is coming after all, and the night
 * re-resolves to whoever held it before her (D-330).
 */
export function ComingAfterAll({
  requestId,
  state,
  onDone,
}: {
  requestId: string
  state: CoverState
  onDone: () => void
}) {
  const run = useMutation({
    mutationFn: () => withdrawCover(requestId),
    onSuccess: onDone,
  })

  if (state === 'Withdrawn') return null

  return (
    <>
      <button
        type="button"
        onClick={() => run.mutate()}
        disabled={run.isPending}
        /*
         * `py-1.5` for the tap target rather than the look: this rendered
         * 119x19 on the panel and the floor is 24px. It was invisible to the
         * layout sweep for as long as the panel had nothing in it (D-254).
         */
        className="mt-2 -mx-1 px-1 py-1.5 text-[12.5px] font-semibold text-accent hover:underline disabled:opacity-60"
      >
        {run.isPending ? 'Saying so...' : 'I can make it after all'}
      </button>

      {/*
        The server writes the useful sentence — "That session has already
        started" — and swallowing it leaves her tapping a button that visibly
        does nothing.
      */}
      {run.isError && (
        <p role="alert" className="text-[12px] text-crit">
          {(run.error as Error).message}
        </p>
      )}
    </>
  )
}
